import { CustomGridPageBase } from "../../common/custom-grid-page.base";
import { autoinject, BindingEngine, transient } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { DataType, QueryFilter, SourceType } from "../../../../models/queries/queries.models";
import * as moment from "moment";
import { Guid } from "../../../../utilities/guid";
import { PipelineExecutionsService } from "../../../../services/pipeline-executions/pipeline-executions-serivce";
import { PipelineExecutionMetricsRequest, PipelineExecutionMetricsResponse as PipelineExecutionMetricsResponse } from "./pipeline-executions.models";
import { DataGridColumn } from "@rightship-tech/common-ui/components/comp-data-grid/comp-data-grid.models";
// import KeyDownEvent = JQuery.KeyDownEvent;

@autoinject()
@transient()
export class PipelineExecutions extends CustomGridPageBase {

    filter: string;
    appliedFilter: string;
    status: string;
    isMobileFilterOpen: boolean;
    pipelineExecutionMetrics: PipelineExecutionMetricsResponse;

    filterKeyDown() {
        this.appliedFilter = this.filter;
        this.setItemSource();
        this.getPipelineExecutionMetrics(this.filter);
    }

    hideMobileFilters() {
        this.isMobileFilterOpen = false;
    }

    toggleMobileFilterDialog() {
        this.isMobileFilterOpen = !this.isMobileFilterOpen;
    }

    applyAllFiltersMobile() {
        this.setItemSource();
        this.hideMobileFilters();
    }
    
    statusFilter(status) {
        if (this.status == status)
            this.status = ""; //Uncheck the current status
        else
            this.status = status;

        //Immediately update ItemSource on desktop (Not on mobile)
        this.setItemSource();
    }
    
    statusFilterMobile(status) { 
        if(status == '')
        {
            if(this.dataGridVM)
            {
                this.dataGridVM.clearSorting();
            }
        } 
        if (this.status == status)
            this.status = ""; //Uncheck the current status
        else
            this.status = status;
    }

    clearFilter() {
        this.filter = "";
        this.appliedFilter = this.filter;
        this.setItemSource();
    }

    constructor(i18N: I18N, protected bindingEngine: BindingEngine,
        private pipelineExecutionsService: PipelineExecutionsService) {

        super(i18N, 'Lists', bindingEngine);
        this.defaultPageCount = 50;
        const columns: DataGridColumn[] = [];
        columns.push(
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.CustomTemplate,
                isSortable: true,
                instancePerRow: true,
                name: 'name',
                title: 'Name',
                value: '<span if.bind="!format(item)">${item.name}</span><a class="link" if.bind="format(item)" href="/pipeline-execution/${format(item)}">${item.name}</a> ',
                parameter: {
                    item: null,
                    format: (item) => {
                        if (item.name.indexOf("(") > -1) {
                            const m = item.name.match(/\(([^)]+)\)/);
                            return m && m.length > 1 ? m[1] : null;
                        }
                        return item.name;
                    }
                }
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.DateTime,
                isSortable: true,
                instancePerRow: true,
                name: 'executionStartDate',
                title: 'Date',
                isAscending: false,
                valueConverter: (value, item) => moment(item.executionStartDate).format("DD MMM YYYY HH:mm:ss") + (item.executionFinishDate ? " - " + moment(item.executionFinishDate).format("DD MMM YYYY HH:mm:ss") : '')
            },
            {
                sortOrder: 0,
                columnOrder: 0,
                dataType: DataType.Default,
                isSortable: true,
                name: 'numberOfProcesses',
                title: 'Number Of Processes'
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.Boolean,
                isSortable: true,
                name: 'hasWarnings',
                title: 'Has Warnings'
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.Boolean,
                isSortable: true,
                name: 'hasWarningsCleared',
                title: 'Warnings Cleared'
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.Boolean,
                isSortable: true,
                name: 'requiresAction',
                title: 'Requires Action'
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.Default,
                isSortable: true,
                width: '100px',
                name: 'pipelineOutcome',
                title: 'Pipeline Outcome'
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.Default,
                isSortable: true,
                width: '200px',
                name: 'pipelineOutcomeDetails',
                title: 'Outcome Details'
            }, {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.CustomTemplate,
                isSortable: true,
                instancePerRow: true,
                name: 'url',
                title: 'View',
                value: '<a class="link" target="_blank" href="api/v1/MdmPipeline/ViewInDashboard/${format(item)}">View Job Detail</a> ',
                parameter: {
                    item: null,
                    format: (item) => {
                        if (item.name) {
                            const m = item.name.match(/\(.*_(?<jobId>.*)\)/);
                            return m && m.length > 1 ? m[1] : null;
                        }
                        return item.name;
                    }
                }
            });

        columns.forEach((column, index) => {
            column.columnOrder = index;
            column.sortOrder = index;
        });

        this.columns = columns;
        this.setItemSource();
        this.getPipelineExecutionMetrics(null);
    }

    getPipelineExecutionMetrics(filter: string) {
        this.pipelineExecutionsService.getPipelineExecutionMetrics({ filter: filter } as PipelineExecutionMetricsRequest)
            .then((response: PipelineExecutionMetricsResponse) => {
                this.pipelineExecutionMetrics = response[0];
            }).catch(e => {
                console.error("Failed To load PipelineExecutionMetrics. Error: " + e.message);
            });
    }

    setItemSource() {
        const currentFilters: QueryFilter[] = [];
        if (this.appliedFilter) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("name", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("pipelineOutcome", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("pipelineOutcomeDetails", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
        }
        switch (this.status) {
            case 'Failed':
                currentFilters.push(QueryFilter.getQuery('isSuccessful', 0, 'eq', DataType.Number, true, false));
                break;
            case 'Successful':
                currentFilters.push(QueryFilter.getQuery('isSuccessful', 1, 'eq', DataType.Number, true, false));
                break;
            case 'Warnings':
                currentFilters.push(QueryFilter.getQuery('hasWarnings', true, 'eq', DataType.Boolean, true, false));
                break;
            case 'Rescheduled':
                currentFilters.push(QueryFilter.getQuery('isRescheduled', true, 'eq', DataType.Boolean, true, false));
                break;
            case 'Queued':
                currentFilters.push(QueryFilter.getQuery('executionGroupFull', "null", 'eq', DataType.Lookup, true, false));
                break;
            case 'InProgress':
                currentFilters.push(QueryFilter.getQuery('executionGroupFull', "null", 'ne', DataType.Lookup, true, false));
                currentFilters.push(QueryFilter.getQuery('isRescheduled', false, 'eq', DataType.Boolean, true, false));
                currentFilters.push(QueryFilter.getQuery('isSuccessful', -1, 'eq', DataType.Number, true, false));
                currentFilters.push(QueryFilter.getQuery('hasWarnings', false, 'eq', DataType.Boolean, true, false));
                break;
        }
        this.itemSource = {
            sourceType: SourceType.ODataUrl,
            source: "odata/PipelineExecutions",
            columns: this.columns,
            initialFilters: currentFilters
        };
    }
}